import { useEffect, useState } from 'react';
import { Users, Sparkles, Heart, Compass, BookOpen } from 'lucide-react';
import { routes } from '@/lib/routes';
import { api } from '@/lib/api';
import type { ToolKey } from '@/lib/tools';
import { useAuthStore } from '@/store/useAuthStore';
import { ToolCard } from './ToolCard';

interface ToolDef {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  status: 'active' | 'coming-soon';
  href?: string;
}

const TOOLS: ToolDef[] = [
  {
    id: 'inner-team',
    name: 'Inneres Team',
    description: 'Erkunde deine inneren Stimmen nach Schulz von Thun und lass sie zu Wort kommen.',
    icon: <Users size={24} />,
    status: 'active',
    href: routes.innerTeam.home(),
  },
  {
    id: 'inner-strengths',
    name: 'InnerStrengths',
    description: 'Stärken und Talente sichtbar machen.',
    icon: <Sparkles size={24} />,
    status: 'coming-soon',
  },
  {
    id: 'inner-values',
    name: 'InnerValues',
    description: 'Die eigenen Wertvorstellungen entdecken.',
    icon: <Heart size={24} />,
    status: 'coming-soon',
  },
  {
    id: 'inner-purpose',
    name: 'InnerPurpose',
    description: 'Erforsche deinen Purpose, dein Why.',
    icon: <Compass size={24} />,
    status: 'coming-soon',
  },
  {
    id: 'inner-journal',
    name: 'InnerJournal',
    description: 'Geführtes Tagesjournal mit reflektierenden Fragen.',
    icon: <BookOpen size={24} />,
    status: 'coming-soon',
  },
];

export function ToolHub() {
  const user = useAuthStore((s) => s.user);
  const [grantedKeys, setGrantedKeys] = useState<Set<ToolKey> | null>(null);

  useEffect(() => {
    // Anonymous visitors see all active tools as clickable (the click leads to
    // the tool, which triggers the login flow via the app's normal auth gate).
    // Only fetch the granted-filter for authenticated users.
    if (!user) {
      setGrantedKeys(null);
      return;
    }

    let cancelled = false;
    api.me
      .tools()
      .then((res) => {
        if (!cancelled) setGrantedKeys(new Set(res.toolKeys));
      })
      .catch(() => {
        // Session expired between mount and fetch → fall back to clickable
        // (no worse than treating them as anonymous).
        if (!cancelled) setGrantedKeys(null);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  return (
    <div className="min-h-dvh bg-background">
      <div className="mx-auto max-w-5xl px-4 py-12 md:px-8 md:py-20">
        <div className="mb-10 max-w-2xl">
          <p className="text-xs font-medium uppercase tracking-wider text-primary">
            Teamity
          </p>
          <h1 className="mt-2 text-3xl font-semibold text-foreground md:text-4xl">
            Willkommen in deinem Werkzeugkasten.
          </h1>
          <p className="mt-3 text-base text-muted-foreground">
            Wähle ein Werkzeug, mit dem du heute arbeiten möchtest. Weitere Tools sind in Vorbereitung.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          {TOOLS.map((tool) => (
            <ToolCard
              key={tool.id}
              {...tool}
              granted={grantedKeys === null ? undefined : grantedKeys.has(tool.id as ToolKey)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
